"use client";

import Link from "next/link";
import { Cloud, CreditCard, KeyRound, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import {
  Code,
  IconArrowRight,
  IconArrowUpRight,
  IconBolt,
  IconCheck,
  IconCloud,
  IconCopy,
  IconGlobe,
  IconHeadset,
  type IconProps,
} from "@/app/_components/icons";

const CLOUD_FACTS = [
  {
    value: "$5",
    label: "in free credits added monthly",
  },
  {
    value: "$0.06",
    label: "per 1K images for cloud inference",
  },
  {
    value: "1 API",
    label: "from prototype to hosted production",
  },
] as const;

const CLOUD_STEPS = [
  {
    Icon: IconCopy,
    title: "Use the same SDK",
    body: "Keep the Moondream API surface your team already built against. Local tests, Lens fine-tunes, Photon deployments, and cloud inference share the same mental model.",
  },
  {
    Icon: IconBolt,
    title: "Skip the serving layer",
    body: "No GPU provisioning, autoscaling work, or queue plumbing. Send images to the managed endpoint and let Moondream handle the production runtime.",
  },
  {
    Icon: IconGlobe,
    title: "Move when you need to",
    body: "Start hosted, then shift workloads to Photon, partner clouds, or private infrastructure when compliance, latency, or cost calls for it.",
  },
] as const;

const TRUST_ITEMS = [
  "HIPAA compliant for healthcare and life-science teams handling sensitive workflows.",
  "SOC 2 is in progress, with the same security program backing our managed production stack.",
  "Enterprise support is available when you need deeper reviews, procurement, or deployment guidance.",
] as const;

const CloudFactCard = ({
  value,
  label,
}: {
  value: string;
  label: string;
}) => (
  <div className="model-card model-card-cyan">
    <div className="model-card-head">
      <h3 className="tabular">{value}</h3>
      <span>Cloud</span>
    </div>
    <p>{label}</p>
  </div>
);

const CloudStep = ({
  Icon,
  title,
  body,
}: {
  Icon: (props: IconProps) => React.ReactNode;
  title: string;
  body: string;
}) => (
  <div className="lens-feat">
    <div className="lens-icon">
      <Icon size={20} />
    </div>
    <div>
      <div className="t-heading-sm">{title}</div>
      <p
        className="t-body-md"
        style={{ color: "var(--text-secondary)", marginTop: 4 }}
      >
        {body}
      </p>
    </div>
  </div>
);

const CloudTerminal = () => (
  <div className="hero-install">
    <span className="hero-install-prompt mono">$</span>
    <span className="hero-install-cmd mono">export MOONDREAM_API_KEY=md_...</span>
    <Link href="/c/cloud/api-keys" className="hero-install-copy" aria-label="Get API key">
      <KeyRound size={14} />
    </Link>
  </div>
);

const HeroVisual = () => (
  <div className="grid-bg" style={{ padding: 24, overflow: "hidden" }}>
    <div>
      <div className="hero-eyebrows">
        <span className="chip chip-accent">
          <span className="dot" /> Managed API
        </span>
        <span className="chip">Autoscaled inference</span>
      </div>

      <div style={{ marginTop: 20 }}>
        <Code
          lang="python"
          tokens={[
            ["k", "import"],
            " moondream ",
            ["k", "as"],
            " md\n\n",
            "model = md.",
            ["f", "vl"],
            "(api_key=",
            ["s", '"$MOONDREAM_API_KEY"'],
            ")\n",
            "image = ",
            ["f", "open"],
            "(",
            ["s", '"frame.jpg"'],
            ", ",
            ["s", '"rb"'],
            ")\n\n",
            "answer = model.",
            ["f", "query"],
            "(image, ",
            ["s", '"Is the line blocked?"'],
            ")\n",
            ["f", "print"],
            "(answer)\n",
          ]}
        />
      </div>

      <div className="concerns" style={{ marginTop: 16, marginBottom: 0 }}>
        <div className="concern" style={{ padding: 18 }}>
          <div className="concern-icon" style={{ marginBottom: 12 }}>
            <Cloud size={20} />
          </div>
          <div className="t-heading-sm">Hosted</div>
          <p className="t-body-sm" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
            Production Moondream without server work.
          </p>
        </div>
        <div className="concern" style={{ padding: 18 }}>
          <div className="concern-icon" style={{ marginBottom: 12 }}>
            <CreditCard size={20} />
          </div>
          <div className="t-heading-sm">Usage based</div>
          <p className="t-body-sm" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
            Start free, then pay as traffic grows.
          </p>
        </div>
        <div className="concern" style={{ padding: 18 }}>
          <div className="concern-icon" style={{ marginBottom: 12 }}>
            <ShieldCheck size={20} />
          </div>
          <div className="t-heading-sm">Compliant</div>
          <p className="t-body-sm" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
            HIPAA compliant. SOC 2 soon.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export function CloudPage() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  return (
    <div className="md-landing">
      <Nav theme={theme} onToggleTheme={toggleTheme} />

      <main>
        <section className="hero section">
          <div className="container hero-grid">
            <div className="hero-col-text">
              <div className="hero-eyebrows">
                <span className="chip chip-accent">
                  <span className="dot" /> Moondream Cloud
                </span>
                <span className="chip">HIPAA compliant · SOC 2 soon</span>
              </div>

              <h1 className="hero-headline t-display-xl" style={{ marginTop: 28 }}>
                Hosted vision inference, ready when production is.
              </h1>
              <p
                className="hero-sub t-body-lg"
                style={{
                  color: "var(--text-secondary)",
                  maxWidth: 560,
                  marginTop: 24,
                }}
              >
                Moondream Cloud gives you the same production-ready VLM through
                a managed API. Start with free monthly credits, ship without
                infrastructure work, and keep a path to local or private
                deployments when your stack evolves.
              </p>

              <div className="hero-ctas" style={{ marginTop: 36 }}>
                <Link href="/c/cloud" className="btn btn-primary">
                  Get an API key <IconArrowRight size={14} />
                </Link>
                <a
                  href="https://docs.moondream.ai"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-secondary"
                >
                  Read the docs <IconArrowUpRight size={14} />
                </a>
              </div>

              <div style={{ marginTop: 24 }}>
                <CloudTerminal />
              </div>
            </div>

            <div className="hero-col-demo">
              <HeroVisual />
            </div>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 48 }}>
          <div className="container">
            <div className="model-card-grid">
              {CLOUD_FACTS.map((fact) => (
                <CloudFactCard key={fact.value} {...fact} />
              ))}
            </div>
          </div>
        </section>

        <div className="journey" data-journey="rail">
          <section className="section" id="cloud-api">
            <div className="container">
              <div className="step-head">
                <div className="step-num mono">01</div>
                <div className="try-intro-grid">
                  <div className="try-intro-heading">
                    <div className="t-eyebrow" style={{ color: "var(--accent-text)" }}>
                      Ship faster
                    </div>
                    <h2 className="t-heading-xl" style={{ marginTop: 10, maxWidth: 720 }}>
                      One managed endpoint for captions, queries, detection, and more.
                    </h2>
                  </div>
                  <div>
                    <p
                      className="t-body-lg"
                      style={{
                        color: "var(--text-secondary)",
                        marginTop: 0,
                        maxWidth: 640,
                      }}
                    >
                      Cloud is the fastest way to put Moondream behind a real
                      product. Use it for prototypes, production services, or
                      burst capacity while you decide where each workload should
                      live long term.
                    </p>
                    <div className="step-actions">
                      <Link href="/c/cloud/api-keys" className="btn btn-primary">
                        Create an API key <IconArrowRight size={14} />
                      </Link>
                      <Link href="/pricing" className="btn btn-secondary">
                        See pricing
                      </Link>
                    </div>
                  </div>
                  <div className="try-intro-code">
                    <Code
                      lang="python"
                      tokens={[
                        ["c", "# Swap in Cloud when you need hosted inference\n"],
                        ["k", "from"],
                        " moondream ",
                        ["k", "import"],
                        " vl\n\n",
                        "model = vl(runtime=",
                        ["s", '"cloud"'],
                        ", api_key=MD_KEY)\n",
                        "result = model.",
                        ["f", "detect"],
                        "(image, ",
                        ["s", '"damaged package"'],
                        ")\n",
                        ["f", "print"],
                        "(result.objects)\n",
                      ]}
                    />
                  </div>
                </div>
              </div>

              <div className="lens-grid" style={{ marginTop: 48 }}>
                <div className="lens-rail">
                  {CLOUD_STEPS.map((step) => (
                    <CloudStep key={step.title} {...step} />
                  ))}
                </div>
                <div className="honest-card" style={{ marginTop: 0 }}>
                  <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
                    Built for production paths
                  </div>
                  <h3 className="t-heading-md" style={{ marginTop: 10 }}>
                    Cloud now. Photon or private deployments later.
                  </h3>
                  <p
                    className="t-body-md"
                    style={{ color: "var(--text-secondary)", marginTop: 10 }}
                  >
                    Moondream Cloud is part of the same platform story as the
                    open models, Lens, and Photon. You can validate in the
                    hosted API, fine-tune for your data, and keep the option to
                    run closer to your users or data when it matters.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="section" id="security">
            <div className="container">
              <div className="step-head">
                <div className="step-num mono">02</div>
                <div>
                  <div className="t-eyebrow" style={{ color: "var(--accent-text)" }}>
                    Trust and compliance
                  </div>
                  <h2 className="t-heading-xl" style={{ marginTop: 10, maxWidth: 720 }}>
                    Bring Moondream into sensitive production workflows.
                  </h2>
                  <p
                    className="t-body-lg"
                    style={{
                      color: "var(--text-secondary)",
                      marginTop: 14,
                      maxWidth: 640,
                    }}
                  >
                    Moondream Cloud is HIPAA compliant today for teams working
                    with regulated healthcare data, and SOC 2 is coming soon as
                    our formal audit program completes.
                  </p>
                </div>
              </div>

              <div className="support-main card">
                <div className="support-col">
                  <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
                    Security posture
                  </div>
                  <ul className="support-list whole">
                    {TRUST_ITEMS.map((item) => (
                      <li key={item}>
                        <IconCheck size={16} /> {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="support-divider" />
                <div className="support-col">
                  <div className="t-eyebrow" style={{ color: "var(--accent-text)" }}>
                    Enterprise help
                  </div>
                  <h3 className="t-heading-md" style={{ marginTop: 10 }}>
                    Need a review before you deploy?
                  </h3>
                  <p
                    className="t-body-md"
                    style={{ color: "var(--text-secondary)", marginTop: 10 }}
                  >
                    We can help your team work through security questionnaires,
                    architecture reviews, and the right deployment shape for
                    regulated or high-volume environments.
                  </p>
                  <a
                    href="mailto:hello@moondream.ai"
                    className="btn btn-secondary"
                    style={{ marginTop: 24 }}
                  >
                    Talk to us <IconHeadset size={14} />
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>

        <section className="section final-cta">
          <div className="container">
            <div className="final-card card">
              <div className="final-inner">
                <div>
                  <div className="t-eyebrow" style={{ color: "var(--accent-text)" }}>
                    Start hosted
                  </div>
                  <h2 className="t-heading-xl" style={{ marginTop: 10 }}>
                    Get a key, send an image, and keep moving.
                  </h2>
                  <p
                    className="t-body-lg"
                    style={{
                      color: "var(--text-secondary)",
                      marginTop: 12,
                      maxWidth: 620,
                    }}
                  >
                    Moondream Cloud is the quickest way to run production VLM
                    inference while keeping the deployment flexibility the main
                    platform is built around.
                  </p>
                </div>
                <div className="final-ctas">
                  <Link href="/c/cloud" className="btn btn-primary">
                    Get started <IconCloud size={14} />
                  </Link>
                  <a
                    href="https://docs.moondream.ai"
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-secondary"
                  >
                    Docs <IconArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
